import React, { useState, useEffect, useRef } from 'react';
import WpAPI from '../../WpApi';

const STORAGE_KEY = 'ro_shutdown_banner_dismissed';
const FALLBACK_DEADLINE = '2026-05-11';

const formatDeadline = (deadlineStr) => {
    const d = new Date((deadlineStr || FALLBACK_DEADLINE) + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const InfoIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
        <circle cx="10" cy="10" r="9" stroke="white" strokeWidth="1.5" />
        <rect x="9.25" y="8.5" width="1.5" height="6" rx="0.75" fill="white" />
        <circle cx="10" cy="6" r="1" fill="white" />
    </svg>
);

const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M14 4L4 14M4 4L14 14" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

/**
 * Shutdown disclaimer banner shown in the header and app.
 *
 * Props:
 *   className {string} — extra CSS class(es) applied to the root element.
 *
 * Reads shutdown deadline from the server REST endpoint automatically.
 * Hidden once the user dismisses it (stored in localStorage).
 */
// Injected once at module load — hides the banner in all print/download contexts.
(() => {
    if (typeof document === 'undefined') return;
    const id = 'shutdown-banner-print-style';
    if (!document.getElementById(id)) {
        const tag = document.createElement('style');
        tag.id = id;
        tag.textContent = '@media print{.shutdown-banner{display:none !important;}}';
        document.head.appendChild(tag);
    }
})();

const TransitionBanner = ({ className }) => {
    const [visible, setVisible] = useState(false);
    const [deadline, setDeadline] = useState(FALLBACK_DEADLINE);
    const bannerRef = useRef(null);

    useEffect(() => {
        if (localStorage.getItem(STORAGE_KEY)) return;

        WpAPI.getTransitionStatus()
            .then((status) => {
                if (status && status.deadline) setDeadline(status.deadline);
                setVisible(true);
            })
            .catch(() => {
                setVisible(true);
            });
    }, []);

    useEffect(() => {
        const styleTag = document.getElementById('shutdown-banner-offset-style') ||
            (() => {
                const tag = document.createElement('style');
                tag.id = 'shutdown-banner-offset-style';
                document.head.appendChild(tag);
                return tag;
            })();

        if (!visible) {
            document.body.classList.remove('has-shutdown-banner');
            styleTag.textContent = '';
            return;
        }

        document.body.classList.add('has-shutdown-banner');

        const el = bannerRef.current;
        if (!el) return;

        // Inject reverse-positioning CSS:
        // back-link stays fixed at top; banner gets margin-top = back-link height so it sits below.
        const applyBackLinkOffset = (backLinkEl) => {
            const backLinkHeight = backLinkEl ? backLinkEl.offsetHeight || 47 : 0;
            if (backLinkHeight > 0) {
                styleTag.textContent =
                    `@media screen and (max-width:1050px){` +
                    `.has-shutdown-banner .back-link{top:0 !important;}` +
                    `.has-shutdown-banner .shutdown-banner{margin-top:${backLinkHeight}px;}` +
                    `}`;
            } else {
                styleTag.textContent = '';
            }
        };

        // Run immediately in case back-link already exists (e.g. page re-render).
        applyBackLinkOffset(document.querySelector('.back-link'));

        // Watch for .back-link entering the DOM (renders after Suspense resolves).
        const mutationObserver = new MutationObserver(() => {
            const backLinkEl = document.querySelector('.back-link');
            applyBackLinkOffset(backLinkEl);
            // Once found, no need to keep watching DOM mutations.
            if (backLinkEl) mutationObserver.disconnect();
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        // Also re-apply on banner resize (text may reflow on narrow screens).
        const resizeObserver = new ResizeObserver(() => {
            applyBackLinkOffset(document.querySelector('.back-link'));
        });
        resizeObserver.observe(el);

        return () => {
            mutationObserver.disconnect();
            resizeObserver.disconnect();
            document.body.classList.remove('has-shutdown-banner');
            styleTag.textContent = '';
        };
    }, [visible]);

    const dismiss = () => {
        localStorage.setItem(STORAGE_KEY, '1');
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            ref={bannerRef}
            className={`shutdown-banner no-print${className ? ' ' + className : ''}`}
            role="alert"
            style={{ background: '#285385', width: '100%', position: 'relative', padding: '10px 52px', boxSizing: 'border-box' }}
        >
            <div
                className="shutdown-banner__inner"
                style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 auto', width: 'fit-content', maxWidth: '100%' }}
            >
                <span className="shutdown-banner__icon" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <InfoIcon />
                </span>
                <p
                    className="shutdown-banner__text"
                    style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 400, lineHeight: 1.5, color: '#fff', margin: 0 }}
                >
                    We are shutting down this site on{' '}
                    <strong>{formatDeadline(deadline)}</strong>.{' '}
                    To make sure you keep your work, please download your resumes before that date.
                </p>
            </div>
            <button
                className="shutdown-banner__close"
                onClick={dismiss}
                aria-label="Dismiss shutdown notice"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', padding: '4px', cursor: 'pointer', opacity: 0.85, lineHeight: 0, position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}
            >
                <CloseIcon />
            </button>
        </div>
    );
};

export default TransitionBanner;
